import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Syringe, MapPin } from 'lucide-react';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const CalendarTab = ({ injections }) => {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState(null);

  const byDate = useMemo(() => {
    const map = {};
    injections.forEach(inj => {
      const key = (inj.injected_at || '').slice(0, 10);
      if (key) (map[key] = map[key] || []).push(inj);
    });
    return map;
  }, [injections]);

  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const firstDay = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const dateKey = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const move = (dir) => {
    setSelected(null);
    setCursor(c => {
      const m = c.m + dir;
      if (m < 0) return { y: c.y - 1, m: 11 };
      if (m > 11) return { y: c.y + 1, m: 0 };
      return { ...c, m };
    });
  };

  const selectedInjections = selected ? (byDate[selected] || []) : [];

  return (
    <div data-testid="calendar-tab">
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => move(-1)} className="text-gray-400 hover:text-white p-2 border border-[#27272A] hover:border-[#C41E3A] rounded-sm transition-colors" data-testid="calendar-prev">
            <ChevronLeft size={16} />
          </button>
          <h3 className="font-['Oswald'] text-base uppercase tracking-[0.15em] text-white" data-testid="calendar-month-label">
            {MONTH_NAMES[cursor.m]} <span className="text-[#D4AF37]">{cursor.y}</span>
          </h3>
          <button onClick={() => move(1)} className="text-gray-400 hover:text-white p-2 border border-[#27272A] hover:border-[#C41E3A] rounded-sm transition-colors" data-testid="calendar-next">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map(w => <div key={w} className="text-center text-[10px] font-bold uppercase tracking-wider text-gray-600 py-1">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const key = dateKey(d);
            const logs = byDate[key] || [];
            const isToday = key === todayKey;
            const isSelected = key === selected;
            return (
              <button
                key={key}
                onClick={() => setSelected(isSelected ? null : key)}
                className={`relative aspect-square flex flex-col items-center justify-center rounded-sm text-sm transition-colors border ${isSelected ? 'border-[#C41E3A] bg-[#C41E3A]/15 text-white' : isToday ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-gray-300 hover:bg-[#151515]'}`}
                data-testid={`calendar-day-${d}`}
              >
                {d}
                {logs.length > 0 && (
                  <span className="flex gap-0.5 mt-0.5">
                    {logs.slice(0, 3).map((_, j) => <span key={j} className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5 mt-4" data-testid="calendar-day-detail">
          <h4 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-3">{selected}</h4>
          {selectedInjections.length === 0 ? (
            <p className="text-gray-500 text-sm">No injections logged this day.</p>
          ) : selectedInjections.map(inj => (
            <div key={inj.injection_id} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0">
              <span className="flex items-center gap-2 text-white text-sm"><Syringe size={13} className="text-[#C41E3A]" /> {inj.peptide_name} <span className="text-gray-500">— {inj.dose}</span></span>
              <span className="flex items-center gap-1 text-gray-400 text-xs"><MapPin size={11} /> {inj.site}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
